import { useQuery } from "@tanstack/react-query";
import React from "react";

import { fmtDuration, getWorkload, type UserWorkload } from "@/api/activity";
import { Button } from "@/components/ui/button";
import { localISODate } from "@/lib/date-range";

/**
 * Загруженность склада (админ).
 *
 * Показывает АКТИВНОЕ время, а не календарное: приход, открытый в 9:00 и
 * закрытый в 14:00, мог стоить сорока минут, и разница дат как мера труда
 * бесполезна. Отрезки работы разрывает пауза длиннее порога бездействия —
 * он настраивается в системных настройках.
 */
export function ActivityPage(): React.ReactElement {
  // Локальные даты, а не UTC: вечером по Москве отчёт открывался бы за
  // вчерашний день (`lib/date-range.ts`).
  const today = localISODate();
  const weekAgo = localISODate(-7);
  const [from, setFrom] = React.useState(weekAgo);
  const [to, setTo] = React.useState(today);
  const [page, setPage] = React.useState("");

  const q = useQuery({
    queryKey: ["activity-workload", from, to, page],
    queryFn: () => getWorkload({ date_from: from, date_to: to, page: page || undefined }),
  });

  const data = q.data;
  const days = React.useMemo(() => {
    const set = new Set<string>();
    data?.users.forEach((u) => Object.keys(u.by_day).forEach((d) => set.add(d)));
    return [...set].sort();
  }, [data]);

  const total = data?.users.reduce((s, u) => s + u.active_seconds, 0) ?? 0;

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Загруженность склада</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Активное время по журналу действий. Пауза дольше{" "}
          {data ? `${data.idle_minutes} мин` : "порога"} не считается работой —
          порог задаётся в системных настройках.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-muted-foreground">
          С<input
            type="date"
            className="ml-1 rounded border p-1.5 text-sm"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="text-xs text-muted-foreground">
          по<input
            type="date"
            className="ml-1 rounded border p-1.5 text-sm"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <select
          className="rounded border p-1.5 text-sm"
          value={page}
          onChange={(e) => setPage(e.target.value)}
        >
          <option value="">Все разделы</option>
          {Object.entries(data?.page_titles ?? {}).map(([key, title]) => (
            <option key={key} value={key}>
              {title}
            </option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
          Обновить
        </Button>
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
      {q.error && <p className="text-sm text-rose-600">Не удалось загрузить отчёт.</p>}

      {data && data.users.length === 0 && (
        <p className="rounded-lg border bg-white p-4 text-sm text-muted-foreground">
          За период действий не было.
        </p>
      )}

      {data && data.users.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="p-2">Кладовщик</th>
                <th className="p-2 text-right">Активное время</th>
                <th className="p-2 text-right">Действий</th>
                <th className="p-2 text-right">Отрезков</th>
                {days.map((d) => (
                  <th key={d} className="p-2 text-right">
                    {d.slice(8)}.{d.slice(5, 7)}
                  </th>
                ))}
                <th className="p-2">Разделы</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u: UserWorkload) => (
                <tr key={u.user_id ?? u.username} className="border-t">
                  <td className="p-2 font-medium">{u.username || "—"}</td>
                  <td className="p-2 text-right font-semibold">
                    {fmtDuration(u.active_seconds)}
                  </td>
                  <td className="p-2 text-right text-slate-500">{u.events}</td>
                  <td className="p-2 text-right text-slate-500">{u.segments}</td>
                  {days.map((d) => (
                    <td key={d} className="p-2 text-right text-slate-600">
                      {u.by_day[d] ? fmtDuration(u.by_day[d]) : ""}
                    </td>
                  ))}
                  <td className="p-2 text-xs text-slate-500">
                    {Object.entries(u.by_page)
                      .filter(([, sec]) => sec > 0)
                      .map(([key, sec]) => `${data.page_titles[key] ?? key}: ${fmtDuration(sec)}`)
                      .join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-slate-50">
              <tr>
                <td className="p-2 font-semibold">Итого</td>
                <td className="p-2 text-right font-semibold">{fmtDuration(total)}</td>
                <td colSpan={2 + days.length + 1} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default ActivityPage;
